import { Suspense, lazy } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { GoogleAnalytics } from "@/components/utils/analytics/GoogleAnalytics";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CookieConsent } from "@/components/layout/CookieConsent";
import { ScrollToTop } from "@/components/utils/ui/ScrollToTop";
import "./i18n";

// Lazy load page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const ReferenceRate = lazy(() => import("./pages/ReferenceRate"));
const ReferenceRateHistory = lazy(() => import("./pages/ReferenceRateHistory"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Checkout = lazy(() => import("./pages/Checkout"));
const RentReductionLetter = lazy(() => import("./pages/RentReductionLetter"));
const InitialRent = lazy(() => import("./pages/InitialRent"));

const queryClient = new QueryClient();

// Language prefixes: empty string = German (default), others use the lang code.
const LANGUAGE_PREFIXES = ["", "en", "fr", "it"] as const;

// Route definitions: path segments and the component to render.
// The root path ("") maps to "/" (or "/<lang>" for prefixed languages).
const ROUTE_DEFINITIONS = [
  { path: "", element: <Index /> },
  { path: "reference-rate", element: <ReferenceRate /> },
  { path: "reference-rate/history", element: <ReferenceRateHistory /> },
  { path: "privacy-policy", element: <PrivacyPolicy /> },
  { path: "cookie-policy", element: <CookiePolicy /> },
  { path: "terms-of-service", element: <TermsOfService /> },
  { path: "checkout", element: <Checkout /> },
  { path: "rent-reduction-letter", element: <RentReductionLetter /> },
  { path: "initial-rent", element: <InitialRent /> },
] as const;

/**
 * Builds the full route path for a given language prefix and route segment.
 * - Default language (prefix "") uses the segment as-is (e.g. "/reference-rate").
 * - Other languages prepend the prefix (e.g. "/en/reference-rate").
 * - Root segments ("") resolve to "/" or "/<prefix>".
 */
function buildPath(prefix: string, segment: string): string {
  if (prefix === "") {
    return segment === "" ? "/" : `/${segment}`;
  }
  return segment === "" ? `/${prefix}` : `/${prefix}/${segment}`;
}

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <LanguageProvider>
              <ScrollToTop />
              <CookieConsentProvider>
                <GoogleAnalytics />
                <CookieConsent />
                <Suspense fallback={null}>
                  <Routes>
                    {LANGUAGE_PREFIXES.flatMap((prefix) =>
                      ROUTE_DEFINITIONS.map(({ path, element }) => (
                        <Route
                          key={buildPath(prefix, path)}
                          path={buildPath(prefix, path)}
                          element={element}
                        />
                      ))
                    )}

                    {/* Catch-all route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </CookieConsentProvider>
            </LanguageProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
